'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  key: string; // 🌟 Conectado con la columna 'module_key' de PostgreSQL
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

// Map de ítems asignando la llave única 'key' que coincide con role_module_permissions
const navItems: NavItem[] = [
  { key: 'dashboard_social', label: 'Dashboard Social', href: '/', icon: 'HomeIcon' },
  { key: 'proyectos', label: 'Dashboard PA-Proyectos', href: '/project-monitoring-dashboard', icon: 'ChartBarIcon' },
  { key: 'proyectos', label: 'Proyectos', href: '/projects', icon: 'FolderOpenIcon' },
  { key: 'socios', label: 'Socios', href: '/member-directory-profiles', icon: 'UsersIcon' },
  { key: 'comites', label: 'Comités', href: '/committee-task-board', icon: 'ClipboardDocumentListIcon', badge: 5 },
  { key: 'finanzas', label: 'Finanzas', href: '/finance-collections', icon: 'BanknotesIcon' },
  { key: 'calendario', label: 'Calendario', href: '/official-calendar-events', icon: 'CalendarDaysIcon' },
  { key: 'pase_lista', label: 'Pase de Lista', href: '/attendance', icon: 'ClipboardDocumentCheckIcon' },
  { key: 'evidencias', label: 'Evidencias Evento', href: '/event-evidences', icon: 'CameraIcon' },
  { key: 'educacion', label: 'Cápsulas de Educación', href: '/education-capsules', icon: 'AcademicCapIcon' },
  { key: 'usuarios', label: 'Usuarios', href: '/user-management', icon: 'UserGroupIcon' },
];

const bottomItems: NavItem[] = [];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  // 🌟 Extraemos 'hasPermission' para evaluar la base de datos en tiempo real
  const { user, userProfile, userRole, hasPermission } = useAuth();
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  // 🌟 FILTRADO DINÁMICO DESDE POSTGRESQL (Reemplaza las reglas fijas previas)
  const visibleNavItems = navItems.filter((item) => hasPermission(item.key));

  const userInitials = userProfile?.full_name
    ? userProfile.full_name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email?.[0]?.toUpperCase() ?? 'U');

  const userName = userProfile?.full_name || user?.email || 'Usuario';
  const userRoleLabel = userRole === 'superusuario' ? 'Superusuario'
    : userRole === 'mesa_directiva' ? 'Mesa Directiva'
    : userRole === 'tesorero' ? 'Tesorero'
    : userRole === 'administracion' ? 'Administración'
    : userRole === 'finanzas' ? 'Finanzas'
    : userRole === 'dama' ? 'Dama' : 'Socio Activo';

  return (
    <aside
      className="fixed left-0 top-0 h-full bg-primary flex flex-col z-[50] transition-all duration-300 ease-in-out overflow-visible"
      style={{ width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10 min-h-[var(--topbar-height)]">
        <div className="flex-shrink-0">
          <AppLogo size={32} className="brightness-0 invert" />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <span className="text-white font-semibold text-sm leading-tight block whitespace-nowrap">
              MetroClub
            </span>
            <span className="text-white/60 text-xs whitespace-nowrap">
              Monterrey Metropolitano
            </span>
          </div>
        )}
      </div>

      {/* Nav Items (Renderizados según permisos dinámicos) */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p className="px-4 text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">
            Principal
          </p>
        )}
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => (
            <li key={`nav-${item.href}`}>
              <Link
                href={item.href}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 relative ${
                  isActive(item.href)
                    ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                title={collapsed ? item.label : undefined}
              >
                <Icon
                  name={item.icon as any}
                  size={20}
                  variant="outline"
                  className="flex-shrink-0"
                />
                {!collapsed && (
                  <span className="text-sm font-medium flex-1 whitespace-nowrap">
                    {item.label}
                  </span>
                )}
                {!collapsed && item.badge && (
                  <span className="bg-accent text-accent-foreground text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                    {item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 bg-accent text-accent-foreground text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
                {isActive(item.href) && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent rounded-r-full" />
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom */}
      <div className="border-t border-white/10 py-3 px-2 space-y-1">
        {bottomItems.map((item) => (
          <Link
            key={`bottom-${item.href}`}
            href={item.href}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150"
            title={collapsed ? item.label : undefined}
          >
            <Icon name={item.icon as any} size={20} variant="outline" className="flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
          </Link>
        ))}

        {/* User Info */}
        <div 
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition-all duration-150 cursor-pointer"
          suppressHydrationWarning
        >
          <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-accent-foreground">{userInitials}</span>
          </div>
          {!collapsed && (
            <div className="overflow-hidden flex-1">
              <p className="text-white text-xs font-semibold whitespace-nowrap truncate">{userName}</p>
              <p className="text-white/50 text-xs whitespace-nowrap">{userRoleLabel}</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 bg-white border border-border text-primary/40 hover:text-primary rounded-full flex items-center justify-center shadow-sm transition-all duration-200 z-[60]"
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
      >
        <Icon
          name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
          size={10}
          variant="solid"
          className="transition-colors"
        />
      </button>
    </aside>
  );
}